import { Link, useLocation } from 'react-router-dom';

export interface RouteTab {
  /** Ruta absoluta a la que navega. La arma quien llama (puede incluir el tenantId). */
  path: string;
  /** Texto visible, en español. El componente no trae copy propio. */
  label: string;
}

interface RouteTabsProps {
  tabs: RouteTab[];
  /** Título de la sección, arriba de las tabs. Opcional. */
  heading?: string;
}

/**
 * Tabs de segundo nivel, gobernadas por la RUTA.
 *
 * Van por ruta y no por estado local para que el shell no tenga que conocer el
 * estado interno de sus páginas — es lo único que hace que un mismo slot sirva
 * para todas. Y trae gratis lo que el estado no: botón atrás, enlaces que se
 * pueden compartir, y refresh que no pierde la sección.
 *
 * OJO: no confundir con `SectionTabs` de `tenant-sections/`, que navega por
 * estado y muestra completitud por sección. Son dos cosas con roles distintos,
 * y por eso ésta se llama distinto.
 */
export function RouteTabs({ tabs, heading }: RouteTabsProps) {
  const { pathname } = useLocation();

  /**
   * Coincide por prefijo CON frontera de segmento.
   *
   * `===` rompería las sub-rutas: estando en `/conversations/abc/123` no
   * quedaría ninguna tab marcada. Un `startsWith` pelado tiene el problema
   * inverso: `/conversations/metrics` marcaría también la tab de
   * `/conversations`. Se exige que lo que sigue al prefijo sea el fin o una
   * barra.
   */
  const coincide = (path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

  // La más específica gana. Sin esto, `/conversations` y
  // `/conversations/metrics` quedarían activas a la vez.
  const activa = tabs
    .filter((t) => coincide(t.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  return (
    <div className="shrink-0 border-b border-edge-subtle bg-surface-base" data-testid="route-tabs">
      {heading ? (
        <h1 className="truncate px-6 pt-4 text-lg font-semibold text-content-primary">{heading}</h1>
      ) : null}
      <nav aria-label="Secciones" className="flex gap-1 px-6">
        {tabs.map((t) => {
          const activo = t.path === activa;
          return (
            <Link
              key={t.path}
              to={t.path}
              aria-current={activo ? 'page' : undefined}
              className={
                activo
                  ? 'border-b-2 border-edge-focus px-3 py-3 text-sm font-medium text-content-primary'
                  : 'border-b-2 border-transparent px-3 py-3 text-sm text-content-tertiary transition-colors hover:text-content-primary'
              }
            >
              {t.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * Tabs de la sección Conversaciones.
 *
 * Viven acá y no duplicadas en cada página para que no diverjan: si una lista
 * "Operadores" y la otra no, la persona pierde la pantalla al cambiar de tab.
 *
 * "Operadores" quedó sin entrada cuando el top-nav bajó de siete ítems a
 * cuatro. Es su sub-sección natural: son métricas DE las conversaciones.
 */
export const CONVERSATION_TABS: RouteTab[] = [
  { path: '/conversations', label: 'Bandeja' },
  { path: '/conversations/metrics', label: 'Operadores' },
];
